import { useEffect, useMemo, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { makeFieldSchema } from '@backstage/plugin-scaffolder-react';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import CircularProgress from '@material-ui/core/CircularProgress';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';

export type TechneDiscoveryUiOptions = {
  /** Plugin Backstage que expõe /discovery/* */
  plugin?: 'oracle-providers' | 'git-providers';
  endpoint: string;
  dependsOn?: string;
  excludeFields?: string[];
  dependsOnParam?: string;
  /** Se true, desabilita edição quando houver exatamente 1 opção */
  lockWhenSingle?: boolean;
  /** Campo com digitação para filtrar (lista longa) */
  searchable?: boolean;
};

type DiscoveryOption = {
  value: string;
  label: string;
  description?: string;
  preferred?: boolean;
};

export const TechneDiscoveryFieldSchema = makeFieldSchema({
  output: z => z.string(),
  uiOptions: z =>
    z.object({
      plugin: z.enum(['oracle-providers', 'git-providers']).optional(),
      endpoint: z.string().min(1),
      dependsOn: z.string().optional(),
      excludeFields: z.array(z.string()).optional(),
      dependsOnParam: z.string().optional(),
      lockWhenSingle: z.boolean().optional(),
      searchable: z.boolean().optional(),
    }),
});

/** @deprecated use TechneDiscoveryFieldSchema */
export const OciSelectFieldSchema = TechneDiscoveryFieldSchema;

function defaultDependsOnParam(endpoint: string): string {
  switch (endpoint) {
    case 'drg-route-tables':
    case 'vpn-cidrs':
      return 'drgId';
    case 'subnet-cidrs':
      return 'parentCidr';
    case 'object-buckets':
      return 'namespace';
    case 'projects':
      return 'organization';
    case 'kubernetes-versions':
    case 'node-shapes':
      return 'region';
    default:
      return 'compartmentId';
  }
}

function pathForEndpoint(endpoint: string): string {
  switch (endpoint) {
    case 'context-tenancy':
    case 'context-region':
      return 'context';
    default:
      return endpoint;
  }
}

function readUiOptions(
  uiSchema: Record<string, unknown> | undefined,
): TechneDiscoveryUiOptions {
  const nested = (uiSchema?.['ui:options'] ?? {}) as Record<string, unknown>;
  return {
    plugin:
      (nested.plugin as TechneDiscoveryUiOptions['plugin']) ||
      (uiSchema?.plugin as TechneDiscoveryUiOptions['plugin']) ||
      'oracle-providers',
    endpoint: String(nested.endpoint ?? uiSchema?.endpoint ?? '').trim(),
    dependsOn: (nested.dependsOn ?? uiSchema?.dependsOn) as string | undefined,
    excludeFields: (nested.excludeFields ??
      uiSchema?.excludeFields) as string[] | undefined,
    dependsOnParam: (nested.dependsOnParam ??
      uiSchema?.dependsOnParam) as string | undefined,
    lockWhenSingle: Boolean(
      nested.lockWhenSingle ?? uiSchema?.lockWhenSingle ?? false,
    ),
    searchable: Boolean(nested.searchable ?? uiSchema?.searchable ?? false),
  };
}

export function TechneDiscoverySelectField(
  props: FieldExtensionComponentProps<string, TechneDiscoveryUiOptions>,
) {
  const {
    onChange,
    rawErrors,
    required,
    schema: { title, description },
    formData,
    uiSchema,
    formContext,
    idSchema,
  } = props;

  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const identityApi = useApi(identityApiRef);

  const options = readUiOptions(uiSchema as Record<string, unknown>);
  const plugin = options.plugin ?? 'oracle-providers';
  const endpoint = options.endpoint;
  const dependsOn = options.dependsOn;
  const excludeFields = options.excludeFields ?? [];
  const dependsOnParam =
    options.dependsOnParam ?? defaultDependsOnParam(endpoint);

  const rootFormData = (formContext?.formData ?? {}) as Record<string, unknown>;
  const dependsOnValue = dependsOn
    ? String(rootFormData[dependsOn] ?? '').trim()
    : '';
  const excludeValues = excludeFields
    .map(field => String(rootFormData[field] ?? '').trim())
    .filter(Boolean)
    .filter(value => value !== formData);

  const dependencyKey = useMemo(
    () =>
      `${plugin}|${endpoint}|${dependsOnValue}|${String(
        rootFormData.ociRegion ?? '',
      )}|${excludeValues.join(',')}`,
    [plugin, endpoint, dependsOnValue, excludeValues, rootFormData.ociRegion],
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<DiscoveryOption[]>([]);

  const needsDependency =
    endpoint === 'drg-route-tables' ||
    endpoint === 'subnet-cidrs' ||
    endpoint === 'object-buckets' ||
    endpoint === 'projects' ||
    endpoint === 'vpn-cidrs';

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!endpoint) {
        setError('ui:options.endpoint obrigatório');
        return;
      }
      if (needsDependency && !dependsOnValue) {
        setItems([]);
        setError(null);
        setLoading(false);
        if (formData) {
          onChange(undefined as unknown as string);
        }
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const baseUrl = await discoveryApi.getBaseUrl(plugin);
        const { token } = await identityApi.getCredentials();
        const params = new URLSearchParams();
        if (dependsOnValue) {
          params.set(dependsOnParam, dependsOnValue);
        }
        if (
          endpoint === 'kubernetes-versions' ||
          endpoint === 'node-shapes'
        ) {
          const tenancyOcid = String(rootFormData.tenancyOcid ?? '').trim();
          if (tenancyOcid.startsWith('ocid1.tenancy.')) {
            params.set('compartmentId', tenancyOcid);
          }
        }
        const selectedRegion = String(rootFormData.ociRegion ?? '').trim();
        if (
          plugin === 'oracle-providers' &&
          selectedRegion &&
          endpoint !== 'regions' &&
          endpoint !== 'context-region' &&
          endpoint !== 'context-tenancy'
        ) {
          params.set('region', selectedRegion);
        }
        // object-buckets também aceita compartmentId do formulário
        if (endpoint === 'object-buckets') {
          const compartmentId = String(
            (rootFormData.okeCompartment as { parentCompartmentOcid?: string } | undefined)
              ?.parentCompartmentOcid ??
              rootFormData.parentCompartmentOcid ??
              '',
          ).trim();
          if (compartmentId) {
            params.set('compartmentId', compartmentId);
          }
        }
        if (excludeValues.length > 0) {
          params.set('exclude', excludeValues.join(','));
        }
        const qs = params.toString();
        const url = `${baseUrl}/discovery/${pathForEndpoint(endpoint)}${
          qs ? `?${qs}` : ''
        }`;
        const response = await fetchApi.fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (!response.ok) {
          const payload = (await response.json().catch(() => ({}))) as {
            error?: string;
          };
          throw new Error(
            payload.error ||
              `Falha ao consultar ${plugin} (${response.status})`,
          );
        }
        const payload = (await response.json()) as {
          tenancyId?: string;
          region?: string;
          organization?: string;
          options?:
            | DiscoveryOption[]
            | {
                tenancy?: DiscoveryOption[];
                region?: DiscoveryOption[];
              };
        };

        let next: DiscoveryOption[] = [];
        if (endpoint === 'context-tenancy') {
          const nested = payload.options;
          next = Array.isArray(nested)
            ? []
            : nested?.tenancy ??
              (payload.tenancyId
                ? [{ value: payload.tenancyId, label: payload.tenancyId }]
                : []);
        } else if (endpoint === 'context-region') {
          const nested = payload.options;
          next = Array.isArray(nested)
            ? []
            : nested?.region ??
              (payload.region
                ? [{ value: payload.region, label: payload.region }]
                : []);
        } else {
          next = Array.isArray(payload.options) ? payload.options : [];
        }

        if (cancelled) return;
        setItems(next);

        if (next.length === 1 && !formData) {
          onChange(next[0].value);
        } else if (!formData) {
          const preferred = next.find(item => item.preferred);
          if (preferred) {
            onChange(preferred.value);
          }
        } else if (formData && !next.some(item => item.value === formData)) {
          onChange(undefined as unknown as string);
        }
      } catch (err) {
        if (cancelled) return;
        setItems([]);
        setError(
          err instanceof Error ? err.message : 'Erro ao carregar opções',
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dependencyKey, discoveryApi, fetchApi, identityApi]);

  const helper =
    error ||
    (needsDependency && !dependsOnValue
      ? dependsOn === 'drgId'
        ? 'Selecione o DRG primeiro'
        : dependsOn === 'adoOrganization'
          ? 'Selecione a organização primeiro'
          : dependsOn === 'ociNamespace'
            ? 'Selecione o namespace primeiro'
            : dependsOn === 'vcnInfraCidr'
              ? 'Informe o CIDR infra primeiro'
              : `Selecione ${dependsOn} primeiro`
      : description) ||
    undefined;

  const lockSingle =
    Boolean(options.lockWhenSingle) && items.length === 1 && Boolean(formData);

  const searchable =
    Boolean(options.searchable) ||
    endpoint === 'compartments' ||
    items.length > 12;

  const selected =
    items.find(item => item.value === formData) ?? null;

  const disabled =
    loading || lockSingle || (needsDependency && !dependsOnValue);

  const labelId = `${idSchema?.$id ?? 'techne'}-label`;

  const optionRow = (item: DiscoveryOption) => (
    <Box>
      <Typography variant="body2">{item.label}</Typography>
      {item.description && item.description !== item.label ? (
        <Typography variant="caption" color="textSecondary">
          {item.description}
        </Typography>
      ) : null}
    </Box>
  );

  if (searchable) {
    return (
      <FormControl
        margin="normal"
        required={required}
        error={(rawErrors?.length ?? 0) > 0 || Boolean(error)}
        fullWidth
        disabled={disabled}
      >
        <Autocomplete
          options={items}
          value={selected}
          disabled={disabled}
          getOptionLabel={item => item.label}
          getOptionSelected={(a, b) => a.value === b.value}
          filterOptions={(opts, state) => {
            const query = state.inputValue.trim().toLowerCase();
            if (!query) return opts;
            return opts.filter(
              item =>
                item.label.toLowerCase().includes(query) ||
                (item.description ?? '').toLowerCase().includes(query) ||
                item.value.toLowerCase().includes(query),
            );
          }}
          onChange={(_event, item) => onChange(item?.value ?? '')}
          renderOption={item => optionRow(item)}
          renderInput={params => (
            <TextField
              {...params}
              label={title}
              required={required}
              error={(rawErrors?.length ?? 0) > 0 || Boolean(error)}
              placeholder={loading ? 'Carregando…' : 'Digite para filtrar'}
              helperText={helper}
              InputLabelProps={{ shrink: true }}
            />
          )}
        />
        {loading && !error && (
          <FormHelperText>Consultando {plugin}…</FormHelperText>
        )}
      </FormControl>
    );
  }

  return (
    <FormControl
      margin="normal"
      required={required}
      error={(rawErrors?.length ?? 0) > 0 || Boolean(error)}
      fullWidth
      disabled={loading || lockSingle || (needsDependency && !dependsOnValue)}
    >
      <InputLabel id={labelId} shrink>
        {title}
        {loading ? '…' : ''}
      </InputLabel>
      <Select
        labelId={labelId}
        label={title}
        value={formData ?? ''}
        displayEmpty
        onChange={event => onChange(String(event.target.value))}
        IconComponent={
          loading
            ? () => (
                <Box mr={1.5} display="flex" alignItems="center">
                  <CircularProgress size={16} />
                </Box>
              )
            : undefined
        }
      >
        {!required && (
          <MenuItem value="">
            <em>Nenhum</em>
          </MenuItem>
        )}
        {loading && items.length === 0 && (
          <MenuItem value="" disabled>
            Carregando…
          </MenuItem>
        )}
        {!loading && items.length === 0 && (
          <MenuItem value="" disabled>
            {error || 'Nenhuma opção disponível'}
          </MenuItem>
        )}
        {items.map(item => (
          <MenuItem key={item.value} value={item.value}>
            {optionRow(item)}
          </MenuItem>
        ))}
      </Select>
      {helper && <FormHelperText>{helper}</FormHelperText>}
      {loading && !error && (
        <FormHelperText>Consultando {plugin}…</FormHelperText>
      )}
    </FormControl>
  );
}

/** Alias usado no template OKE (`ui:field: OciSelect`). */
export const OciSelectField = TechneDiscoverySelectField;
